import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import {
  Box,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Input,
  SELECTOR_VISUAL_RAIL_PX,
  Text,
  type EntitySelection,
  type EntitySelectorConfig,
} from "@salestrak/ui";
import {
  PROPERTY_VALUE_EDIT_LEAD_INSET_CLASS,
  TABLE_VALUE_LEAD_INSET_CLASS,
} from "#app/components/fields/property-value-inset";
import { defaultCountry } from "@salestrak/custom-fields";
import {
  typedCountryCodes,
  countryCodeMap,
  getPhoneCountryIso,
  validatePhoneNumber,
  getExamplePhoneForCountry,
  compositeShellProps,
  type FieldSurface,
  type FieldControlSize,
} from "#app/lib/custom-field-utils";
import {
  CUSTOM_FIELD_MAX_LENGTHS,
  formatNanpAsYouType,
} from "@salestrak/custom-fields";
import type { Omittable } from "@salestrak/ui";
import { toControlSize, scheduleFieldDeactivate } from "./field-control";
import { CountryFlagImage } from "./country-flag-image";

const countryPool = {
  id: "countries",
  label: "Countries",
  items: typedCountryCodes.map((c) => ({
    id: c.country,
    title: c.name,
    keywords: [c.code, c.name, c.country],
    avatar: {
      // Fills the selector row's visual rail, like every other pool.
      render: () => (
        <CountryFlagImage iso={c.country} size={SELECTOR_VISUAL_RAIL_PX} />
      ),
    },
    // The dial code is a persistent fact, not hover chrome — the trigger
    // shows only the flag, so this row meta is where the code is read.
    rowMeta: () => <Text size="sm" color="muted">{c.code}</Text>,
  })),
};

const countryPoolItemsById: Record<
  string,
  (typeof countryPool.items)[number]
> = Object.fromEntries(countryPool.items.map((item) => [item.id, item]));

const countryPickerConfig: EntitySelectorConfig = {
  pools: [countryPool],
  maxSelections: 1,
  searchPlaceholder: "Country code",
  emptyMessage: "No countries found",
};

const isDigit = (ch: string) => ch >= "0" && ch <= "9";
const phoneDigitCount = (text: string) => text.replace(/\D/g, "").length;

// Live grouping for the draft. Only +1 has ONE national presentation this
// package can know without libphonenumber metadata, so only +1 reshapes; any
// other region keeps the text as typed. A draft starting with "+" is an
// international escape and is never reshaped either.
function formatPhoneDraft(text: string, countryKey: string): string {
  if (countryCodeMap[countryKey]?.code !== "+1") return text;
  if (text.trimStart().startsWith("+")) return text;
  return formatNanpAsYouType(text.replace(/\D/g, ""));
}

/** The form-space phone value: the national digits paired with the region. */
export type PhoneFormValue = {
  value: string;
  /** Dial code (`+1`). */
  countryCode: string;
  /** ISO-2 region (`US`). */
  country: string;
};

/**
 * The phone editor — country flag picker + as-you-type national input — shared
 * by the `phone` custom field and the built-in Phone properties (contact,
 * account), so the two cannot present different editing UXs. Speaks the
 * form-space pair on both sides; a host that stores E.164 converts through
 * `dataToField` / `fieldDto`.
 */
export function PhoneInlineInput({
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  invalid,
  surface = "table",
  size,
  onDraftErrorChange,
  inset = "cell",
  enterKeyHint,
  label,
}: Omittable<{
  value: unknown;
  onChangeValue: (value: PhoneFormValue) => void;
  onDeactivate?: () => void;
  autoFocus?: boolean;
  invalid?: boolean;
  surface?: FieldSurface;
  size?: FieldControlSize;
  onDraftErrorChange?: (message: string | null) => void;
  /** Table surface only. `property` leads the flag with the property value
   *  inset so the composite starts where the rest display's text did. */
  inset?: "cell" | "property";
  enterKeyHint?: "next" | "done";
  /** The number input's accessible name: the field it edits. */
  label?: string;
}>) {
  const phoneObj =
    typeof value === "object" && value !== null
      ? (value as { countryCode?: string; value?: string; country?: string })
      : null;

  const [selectedCountryKey, setSelectedCountryKey] = useState(
    () => getPhoneCountryIso(value) || defaultCountry,
  );
  const [phoneText, setPhoneText] = useState(() => {
    const initial =
      phoneObj?.value != null
        ? String(phoneObj.value)
        : value != null
          ? String(value)
          : "";
    return formatPhoneDraft(initial, getPhoneCountryIso(value) || defaultCountry);
  });
  const [isCountrySelectorOpen, setIsCountrySelectorOpen] = useState(false);

  const selectedCountry = countryCodeMap[selectedCountryKey];
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const dirtyRef = useRef(false);
  const phoneTextRef = useRef(phoneText);
  phoneTextRef.current = phoneText;
  const selectedCountryKeyRef = useRef(selectedCountryKey);
  selectedCountryKeyRef.current = selectedCountryKey;
  const countrySelectorOpenRef = useRef(isCountrySelectorOpen);
  countrySelectorOpenRef.current = isCountrySelectorOpen;

  const emitFullPhoneNumber = useCallback(
    (phone: string, countryKey: string) => {
      const country = countryCodeMap[countryKey];
      // The stored half is bare national digits; the live grouping is
      // display-only. A "+"-escaped draft travels as typed.
      const stored = phone.trimStart().startsWith("+")
        ? phone
        : phone.replace(/\D/g, "");
      onChangeValue({ value: stored, countryCode: country?.code ?? "+1", country: countryKey });
    },
    [onChangeValue],
  );

  const emitRef = useRef(emitFullPhoneNumber);
  emitRef.current = emitFullPhoneNumber;

  // Flush unsaved phone value on unmount. Always emit raw — the parent's
  // validation gate decides whether to commit; silently dropping invalid
  // input here was a data-loss bug (user thought they saved).
  useEffect(() => {
    return () => {
      if (dirtyRef.current) {
        emitRef.current(phoneTextRef.current, selectedCountryKeyRef.current);
      }
    };
  }, []);

  // The ring turns red while the draft does not parse as the selected
  // country's number and clears on the digit that completes it. Blur still
  // emits; the parent's validation gate refuses an invalid number and the
  // draft stays here for the user to fix.
  const onDraftErrorChangeRef = useRef(onDraftErrorChange);
  onDraftErrorChangeRef.current = onDraftErrorChange;
  const reportDraftValidity = useCallback((text: string, countryKey: string) => {
    onDraftErrorChangeRef.current?.(
      validatePhoneNumber(text, countryKey) ? null : "Invalid phone number",
    );
  }, []);

  const pendingCaretRef = useRef<number | null>(null);
  const handlePhoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dirtyRef.current = true;
      const input = event.target;
      const raw = input.value;
      const prev = phoneTextRef.current;
      const caret = input.selectionStart ?? raw.length;
      let adjusted = raw;
      let adjustedCaret = caret;
      // A backspace that landed on a formatting character (same digits,
      // shorter text) meant the digit before it — drop that digit too, or
      // the reformat re-inserts the character and the key does nothing.
      if (raw.length < prev.length && phoneDigitCount(raw) === phoneDigitCount(prev)) {
        let i = caret - 1;
        while (i >= 0 && !isDigit(adjusted.charAt(i))) i--;
        if (i >= 0) {
          adjusted = adjusted.slice(0, i) + adjusted.slice(i + 1);
          adjustedCaret = i;
        }
      }
      const next = formatPhoneDraft(adjusted, selectedCountryKey);
      if (next !== raw) {
        // Re-seat the caret after the same COUNT of digits it followed —
        // the controlled write below otherwise snaps it to the end.
        const digitsBefore = phoneDigitCount(adjusted.slice(0, adjustedCaret));
        let pos = 0;
        let seen = 0;
        while (pos < next.length && seen < digitsBefore) {
          if (isDigit(next.charAt(pos))) seen += 1;
          pos += 1;
        }
        pendingCaretRef.current = pos;
      } else {
        pendingCaretRef.current = null;
      }
      setPhoneText(next);
      reportDraftValidity(next, selectedCountryKey);
    },
    [selectedCountryKey, reportDraftValidity],
  );

  useLayoutEffect(() => {
    const pos = pendingCaretRef.current;
    if (pos == null) return;
    pendingCaretRef.current = null;
    inputRef.current?.setSelectionRange(pos, pos);
  }, [phoneText]);

  const handlePhoneBlur = useCallback(() => {
    dirtyRef.current = false;
    emitFullPhoneNumber(phoneText, selectedCountryKey);
    scheduleFieldDeactivate(
      rootRef.current as HTMLElement | null,
      onDeactivate,
      () => countrySelectorOpenRef.current,
    );
  }, [phoneText, selectedCountryKey, emitFullPhoneNumber, onDeactivate]);

  const handleCountrySelect = useCallback(
    (selections: EntitySelection[]) => {
      const selected = selections[0];
      if (!selected) return;
      const country = countryCodeMap[selected.id];
      if (country) {
        setSelectedCountryKey(selected.id);
        // Re-dress the draft in the new region's grouping and re-judge its
        // validity — the same digits can be complete in one region and
        // short in another.
        const nextText = formatPhoneDraft(
          phoneText.trimStart().startsWith("+")
            ? phoneText
            : phoneText.replace(/\D/g, ""),
          selected.id,
        );
        setPhoneText(nextText);
        reportDraftValidity(nextText, selected.id);
        dirtyRef.current = false;
        emitFullPhoneNumber(nextText, selected.id);
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      }
    },
    [phoneText, emitFullPhoneNumber, reportDraftValidity],
  );

  const handleCountrySelectorOpenChange = useCallback(
    (nextOpen: boolean) => {
      setIsCountrySelectorOpen(nextOpen);

      if (nextOpen) return;

      requestAnimationFrame(() => {
        const fieldRoot = rootRef.current as HTMLElement | null;
        const activeElement = document.activeElement as HTMLElement | null;

        if (fieldRoot?.contains(activeElement)) {
          inputRef.current?.focus();
          return;
        }

        scheduleFieldDeactivate(fieldRoot, onDeactivate);
      });
    },
    [onDeactivate],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const entity = countryPoolItemsById[selectedCountryKey];
    if (!entity) return [];
    return [{ id: selectedCountryKey, poolId: countryPool.id, entity }];
  }, [selectedCountryKey]);

  return (
    <Box
      ref={rootRef}
      direction="row"
      align="center"
      fill="flex"
      {...compositeShellProps(surface, size)}
    >
      <EntitySelector
        title="Country"
        config={countryPickerConfig}
        value={selections}
        onValueChange={handleCountrySelect}
        onOpenChange={handleCountrySelectorOpenChange}
      >
        <EntitySelectorTrigger
          render={
            // Flag-only trigger: the dial code and country name live in the
            // popover rows, so the button's name is the accessible label.
            <Box
              as="button"
              direction="row"
              align="center"
              fill="height"
              // The trailing inset IS the flag→number gap: the table input
              // carries no inset of its own. The leading inset puts the flag
              // where the host's rest text starts — the table cell's 8px or
              // the property row's 12px, each less the 1px ring — so it
              // never sits flush against the frame.
              paddingRight="base"
              paddingLeft={surface === "table" ? "none" : "sm"}
              className={
                surface === "table"
                  ? inset === "property"
                    ? PROPERTY_VALUE_EDIT_LEAD_INSET_CLASS
                    : TABLE_VALUE_LEAD_INSET_CLASS
                  : undefined
              }
              shrink={false}
              cursor="pointer"
              aria-label={`Country: ${selectedCountry?.name ?? "United States"} (${selectedCountry?.code ?? "+1"})`}
            >
              <CountryFlagImage iso={selectedCountryKey} size={20} />
            </Box>
          }
        />
        <EntitySelectorContent align="start">
          <EntitySelectorInput />
          <EntitySelectorPool value={countryPool.id} hideLabel />
          <EntitySelectorEmpty>No countries found</EntitySelectorEmpty>
        </EntitySelectorContent>
      </EntitySelector>
      <Box fill="flex" shrink minWidth={60}>
        <Input
          ref={inputRef}
          value={phoneText}
          onChange={handlePhoneChange}
          onBlur={handlePhoneBlur}
          placeholder={getExamplePhoneForCountry(selectedCountryKey)}
          type="tel"
          enterKeyHint={enterKeyHint}
          variant="table"
          // The card's `sm` rung; the default 30px input would push a
          // 28px card row taller on activation.
          size={toControlSize(size)}
          maxLength={CUSTOM_FIELD_MAX_LENGTHS.phone}
          aria-label={label}
          aria-invalid={invalid}
          autoFocus={autoFocus}
        />
      </Box>
    </Box>
  );
}
